import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import './Platforms.css'

const platforms = [
  {
    id: 'aws',
    name: 'Amazon EKS',
    provider: 'AWS',
    tagline: 'Elastic Kubernetes Service',
    color: '#ff9900',
    logo: 'AWS',
    description: 'Amazon Elastic Kubernetes Service is the most widely adopted managed Kubernetes platform. GT Expression delivers battle-tested EKS architectures optimised for cost, security, and operational simplicity.',
    capabilities: [
      { label: 'Managed Node Groups', desc: 'Auto-patching, replacement, and draining with zero manual intervention.' },
      { label: 'Fargate Profiles', desc: 'Serverless pods for batch and cost-sensitive workloads — no node management.' },
      { label: 'EKS Addons', desc: 'VPC CNI, CoreDNS, kube-proxy, and EBS CSI managed natively by AWS.' },
      { label: 'IRSA', desc: 'IAM Roles for Service Accounts — fine-grained pod-level AWS permissions without static keys.' },
      { label: 'Cluster Autoscaler / Karpenter', desc: 'Right-size your fleet in real-time. Karpenter provisions nodes in under 60 seconds.' },
      { label: 'ALB Ingress Controller', desc: 'Native AWS Application Load Balancer integration with WAF and ACM.' },
    ],
    cicd: ['CodePipeline', 'GitHub Actions', 'ArgoCD', 'Flux'],
    iac: 'Terraform AWS EKS module + Helm charts',
  },
  {
    id: 'azure',
    name: 'Azure AKS',
    provider: 'Azure',
    tagline: 'Azure Kubernetes Service',
    color: '#0078d4',
    logo: 'AZ',
    description: 'Azure Kubernetes Service provides deep integration with the Microsoft ecosystem — Active Directory, DevOps, Container Registry, and more. Ideal for enterprises already invested in the Azure platform.',
    capabilities: [
      { label: 'Azure AD Integration', desc: 'OIDC-backed RBAC — Kubernetes access controlled through your existing Azure AD groups.' },
      { label: 'Workload Identity', desc: 'Pod-level managed identity binding — replaces legacy service principal secrets.' },
      { label: 'Virtual Nodes (ACI)', desc: 'Burst pod capacity to serverless Azure Container Instances without provisioning nodes.' },
      { label: 'Azure CNI Overlay', desc: 'Scalable networking that decouples pod IP allocation from the underlying VNet.' },
      { label: 'AGIC', desc: 'Application Gateway Ingress Controller — L7 load balancing with WAF policies.' },
      { label: 'Cluster Node Auto-Provisioner', desc: 'AKS-native replacement for Cluster Autoscaler — Karpenter-inspired intelligent scaling.' },
    ],
    cicd: ['Azure DevOps', 'GitHub Actions', 'ArgoCD', 'Tekton'],
    iac: 'Terraform AzureRM + Bicep modules',
  },
  {
    id: 'gke',
    name: 'Google GKE',
    provider: 'GKE',
    tagline: 'Google Kubernetes Engine',
    color: '#4285f4',
    logo: 'GK',
    description: 'Google Kubernetes Engine is Kubernetes at its origin — Google invented it, and GKE benefits from that deep integration. Autopilot mode delivers fully managed clusters with per-pod billing and hardened security defaults.',
    capabilities: [
      { label: 'GKE Autopilot', desc: 'Fully managed nodes — Google handles provisioning, scaling, security patching, and pod isolation.' },
      { label: 'Workload Identity', desc: 'Kubernetes Service Accounts mapped to Google Service Accounts — no credential management.' },
      { label: 'Binary Authorization', desc: 'Policy enforcement ensuring only signed, verified container images run in production.' },
      { label: 'GKE Gateway API', desc: 'Next-generation L7 routing with multi-cluster support and rich traffic shaping.' },
      { label: 'Config Connector', desc: 'Manage GCP resources (Pub/Sub, BigQuery, Cloud SQL) as Kubernetes custom resources.' },
      { label: 'Multi-cluster Ingress', desc: 'Single global IP routing across multiple GKE clusters in different regions.' },
    ],
    cicd: ['Cloud Build', 'GitHub Actions', 'ArgoCD', 'Cloud Deploy'],
    iac: 'Terraform Google provider + Workload Identity modules',
  },
]

export default function Platforms() {
  const [active, setActive] = useState('aws')
  const platform = platforms.find(p => p.id === active)

  return (
    <main style={{ paddingTop: '80px' }}>
      {/* ── Header ───────────────────────────────── */}
      <section className="section">
        <div className="container platforms-header">
          <span className="badge">Platforms</span>
          <h1>Kubernetes on Every <span className="text-accent">Major Cloud</span></h1>
          <p>GT Expression delivers and operates production Kubernetes across the big three cloud providers. Pick your platform — or let us design a multi-cloud strategy.</p>
        </div>
      </section>

      {/* ── Platform tabs ─────────────────────────── */}
      <section className="section section--alt">
        <div className="container">
          {/* Tab bar */}
          <div className="platforms-tabs">
            {platforms.map(p => (
              <button
                key={p.id}
                className={`platforms-tab ${active === p.id ? 'platforms-tab--active' : ''}`}
                style={active === p.id ? { borderColor: p.color, color: p.color, background: `${p.color}12` } : {}}
                onClick={() => setActive(p.id)}
              >
                <span className="platforms-tab__logo" style={{ background: `${p.color}22`, color: p.color }}>
                  {p.logo}
                </span>
                <span>
                  <strong>{p.name}</strong>
                  <small>{p.tagline}</small>
                </span>
              </button>
            ))}
          </div>

          {/* Platform detail */}
          <div className="platforms-detail" key={active}>
            <div className="platforms-detail__left">
              <h2 style={{ color: platform.color }}>{platform.name}</h2>
              <p className="platforms-detail__desc">{platform.description}</p>

              <div className="platforms-detail__meta">
                <div className="platforms-meta-block">
                  <h4>CI/CD Integrations</h4>
                  <div className="platforms-chips">
                    {platform.cicd.map(t => (
                      <span key={t} className="platforms-chip" style={{ borderColor: `${platform.color}40`, color: platform.color }}>
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="platforms-meta-block">
                  <h4>IaC Stack</h4>
                  <p className="platforms-iac">{platform.iac}</p>
                </div>
              </div>
            </div>

            <div className="platforms-detail__right">
              <h3>Key Capabilities</h3>
              <div className="platforms-capabilities">
                {platform.capabilities.map(c => (
                  <div key={c.label} className="platforms-cap-card" style={{ '--cap-color': platform.color }}>
                    <h4 style={{ color: platform.color }}>{c.label}</h4>
                    <p>{c.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Multi-cloud callout ───────────────────── */}
      <section className="section">
        <div className="container platforms-multicloud">
          <div className="platforms-multicloud__text">
            <span className="badge">Multi-Cloud</span>
            <h2>One Team. Three Clouds.</h2>
            <p>Don't want to be locked into a single provider? GT Expression designs multi-cluster, multi-cloud architectures that give you portability without operational chaos — using GitOps, federated identity, and unified observability.</p>
          </div>
          <div className="platforms-multicloud__logos">
            {platforms.map(p => (
              <div key={p.id} className="platforms-multicloud__badge" style={{ borderColor: `${p.color}40`, color: p.color }}>
                <span className="platforms-tab__logo" style={{ background: `${p.color}22`, color: p.color, fontSize: '18px', width: '44px', height: '44px' }}>
                  {p.logo}
                </span>
                <span>{p.provider}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── About CTA ────────────────────────────── */}
      <section className="section section--alt">
        <div className="container services-cta">
          <h2>Want to know who's behind this?</h2>
          <p>GT Expression is a team of cloud-native engineers with deep hands-on experience across every platform we offer. Learn about our values, approach, and the tools we master.</p>
          <Link to="/about" className="btn btn--primary">Learn More About Us →</Link>
        </div>
      </section>
    </main>
  )
}
